import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

import { MOTION_MS, useReducedMotion } from './motion'

// ============================================================================
// THE PHONE'S BOTTOM SHEET — board 25 ("Open sheet: 360ms spring · dim 42%") and the motion
// spec: "Sheets: one open height that fits the content + closed (no half/full); drag up only
// rubber-bands, drag down > 80px closes."
//
// ONE HEIGHT. The panel is as tall as its content (capped below the top of the screen); there is
// no half-open detent to land in by accident.
//
// THE DRAG WRITES STYLES, NOT STATE. The finger moves the panel through `style.transform` on a
// ref; React hears about it once, on release, and only if the release closes the sheet.
//
// Not yet adopted by the capture sheet (mobile/capture/CaptureSheet.tsx): that sheet keeps its
// current look until its own redesign phase, and swapping its scrim and spring now would change
// it. Phase 2's sheets are its first users: the sync report (board 21) and important mail (23)
// as bottom sheets, and the notifications dialog (22) as `variant="float"` — the same scrim, the
// same spring and the same Escape/scrim close, but hung from the top and not draggable (board 22
// draws it as a floating card with no handle).
// ============================================================================

/** Drag down further than this and release: the sheet closes. */
export const SHEET_CLOSE_PX = 80
/** How far an upward drag can stretch the sheet, at most. */
export const SHEET_RUBBER_MAX_PX = 40

/** Where the panel sits for a finger `dy` px below where it went down. Down is 1:1; up resists. */
export function sheetOffset(dy: number): number {
  if (dy >= 0) return dy
  return -Math.min(SHEET_RUBBER_MAX_PX, Math.sqrt(-dy) * 3)
}

export function sheetShouldClose(dy: number): boolean {
  return dy > SHEET_CLOSE_PX
}

type Phase = 'closed' | 'open' | 'closing'

export function MobileSheet({
  open,
  onClose,
  label,
  children,
  variant = 'bottom',
  panelStyle,
  bodyStyle,
}: {
  open: boolean
  onClose: () => void
  /** The dialog's accessible name. */
  label: string
  children: ReactNode
  /** `bottom`: the draggable bottom sheet. `float`: a card hung from the top, no handle, no drag. */
  variant?: 'bottom' | 'float'
  /** The panel's own geometry and colour, when a board draws it differently from the default. */
  panelStyle?: CSSProperties
  bodyStyle?: CSSProperties
}) {
  const float = variant === 'float'
  const reduced = useReducedMotion()
  const [phase, setPhase] = useState<Phase>(open ? 'open' : 'closed')
  const panel = useRef<HTMLDivElement>(null)
  const scrim = useRef<HTMLDivElement>(null)
  const drag = useRef<{ id: number; y0: number; dy: number } | null>(null)

  useEffect(() => {
    if (open) {
      setPhase('open')
      return
    }
    if (phase !== 'open') return
    setPhase('closing')
    const timer = window.setTimeout(() => setPhase('closed'), reduced ? MOTION_MS.fade : MOTION_MS.sheetOut)
    return () => window.clearTimeout(timer)
    // `phase` is read, not tracked: this runs on `open` flipping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // In and out. Styles go on through refs so the drag (also refs) and the entrance share one source.
  useLayoutEffect(() => {
    const p = panel.current
    const s = scrim.current
    if (!p || !s) return
    if (phase === 'open') {
      if (reduced) {
        p.style.opacity = '0'
        s.style.opacity = '0'
      } else if (float) {
        p.style.transform = 'translateY(-16px) scale(0.98)'
        p.style.opacity = '0'
        s.style.opacity = '0'
      } else {
        p.style.transform = 'translateY(100%)'
        s.style.opacity = '0'
      }
      const frame = requestAnimationFrame(() => {
        p.style.transition = reduced
          ? 'opacity var(--m-dur-fade) linear'
          : float
            ? 'transform var(--m-dur-sheet-in) var(--m-ease-sheet), opacity var(--m-dur-fade) linear'
            : 'transform var(--m-dur-sheet-in) var(--m-ease-sheet)'
        s.style.transition = 'opacity var(--m-dur-sheet-out) linear'
        p.style.transform = ''
        p.style.opacity = ''
        s.style.opacity = ''
      })
      return () => cancelAnimationFrame(frame)
    }
    if (phase === 'closing') {
      p.style.transition = reduced
        ? 'opacity var(--m-dur-fade) linear'
        : float
          ? 'transform var(--m-dur-sheet-out) var(--m-ease-ink), opacity var(--m-dur-sheet-out) linear'
          : 'transform var(--m-dur-sheet-out) var(--m-ease-ink)'
      s.style.transition = 'opacity var(--m-dur-sheet-out) linear'
      if (reduced) p.style.opacity = '0'
      else if (float) {
        p.style.transform = 'translateY(-12px)'
        p.style.opacity = '0'
      } else p.style.transform = 'translateY(105%)'
      s.style.opacity = '0'
    }
  }, [phase, reduced, float])

  useEffect(() => {
    if (phase !== 'open') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, onClose])

  if (phase === 'closed') return null

  return (
    <div
      className="m-sheet-root"
      data-variant={variant}
      data-phase={phase}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div ref={scrim} className="m-sheet-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className="m-sheet bg-m-ground text-m-ink"
        style={panelStyle}
        onPointerDown={(e) => {
          if (float) return
          // Only the grab strip starts a drag; the content keeps its own taps and scrolling.
          if (!(e.target as HTMLElement).closest('[data-sheet-grab]')) return
          drag.current = { id: e.pointerId, y0: e.clientY, dy: 0 }
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            // Synthetic pointers cannot be captured; the drag still works while over the panel.
          }
          e.currentTarget.style.transition = 'none'
        }}
        onPointerMove={(e) => {
          const d = drag.current
          if (!d || d.id !== e.pointerId) return
          d.dy = e.clientY - d.y0
          e.currentTarget.style.transform = `translateY(${sheetOffset(d.dy)}px)`
        }}
        onPointerUp={(e) => {
          const d = drag.current
          drag.current = null
          if (!d) return
          if (sheetShouldClose(d.dy)) {
            onClose()
            return
          }
          e.currentTarget.style.transition = 'transform var(--m-dur-sheet-in) var(--m-ease-sheet)'
          e.currentTarget.style.transform = ''
        }}
        onPointerCancel={(e) => {
          drag.current = null
          e.currentTarget.style.transition = 'transform var(--m-dur-sheet-in) var(--m-ease-sheet)'
          e.currentTarget.style.transform = ''
        }}
      >
        {!float && (
          <div data-sheet-grab="" className="m-sheet-grab">
            <span className="m-sheet-handle" aria-hidden="true" />
          </div>
        )}
        <div className="m-sheet-body" style={bodyStyle}>
          {children}
        </div>
      </div>
    </div>
  )
}
